import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment.public';

export interface UserProfile {
  data: {
    id: string;
    email: string;
    fullName: string;
    roles: string[];
    tenantId: string;
    patientId: string | null;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private baseUrl = environment.authBase;

  // Estado reactivo del usuario actual
  currentUser = signal<UserProfile | null>(null);

  // Indica si ya cargamos el perfil desde backend
  profileLoaded = signal<boolean>(false);

  constructor(private http: HttpClient) {}

  // ===============================
  // CONFIRMAR EMAIL
  // ===============================
  confirmEmail(userId: string, token: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/confirm-email`, {
      params: { userId, token },
      withCredentials: true,
    });
  }

  // ===============================
  // REGISTRO
  // ===============================
  register(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/register`, data, {
      withCredentials: true,
    });
  }

  // ===============================
  // PERFIL INICIAL (AL CARGAR APP)
  // ===============================
  async loadProfileOnInit() {
    try {
      const profile = await firstValueFrom(this.getProfile());
      this.currentUser.set(profile);
    } catch {
      this.currentUser.set(null);
    }

    this.profileLoaded.set(true);
  }

  // ===============================
  // LOGIN (COOKIE HTTP ONLY)
  // ===============================
  login(email: string, password: string): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/login`,
      { email, password },
      { withCredentials: true }
    );
  }

  // ===============================
  // OBTENER PERFIL
  // ===============================
  getProfile(): Observable<UserProfile> {
    return this.http.get<UserProfile>(`${this.baseUrl}/me`, {
      withCredentials: true,
    });
  }

  // ===============================
  // LOGOUT
  // ===============================
  logout() {
    return this.http.post(
      `${this.baseUrl}/logout`,
      {},
      { withCredentials: true }
    );
  }

  // ===============================
  // UTILIDAD: ¿EL USUARIO ACTUAL TIENE ROLES?
  // ===============================
  hasRole(role: string): boolean {
    const user = this.currentUser();
    if (!user) return false;
    return user.data.roles.includes(role);
  }

  getRoles(): string[] {
    const user = this.currentUser();
    return user ? user.data.roles : [];
  }
}
